import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  PhoneCall, CheckCircle, AlertTriangle, ShieldAlert,
  TrendingUp, TrendingDown, Minus, Filter, RefreshCw
} from "lucide-react";
import { cn } from "../lib/utils";
import DateFilter from "../components/DateFilter";
import { PROBLEM_CATEGORIES, classifyCall } from "../lib/callFilters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function computeAnalytics(calls, publisherFilter) {
  const filtered = publisherFilter
    ? calls.filter(c => c.publisher_name === publisherFilter)
    : calls;

  const pubSet = new Set();
  calls.forEach(c => { if (c.publisher_name) pubSet.add(c.publisher_name); });
  const publishers = [...pubSet].sort().map(name => ({ name }));

  let totalCalls = 0, validCalls = 0, problemCalls = 0;
  const dailyMap = {};

  for (const call of filtered) {
    totalCalls++;
    const cls = classifyCall(call);
    if (cls === "valid") validCalls++;
    else if (cls === "problem") problemCalls++;

    const dateStr = call.call_date ? call.call_date.substring(0, 10) : null;
    if (dateStr) {
      if (!dailyMap[dateStr]) dailyMap[dateStr] = { total: 0, valid: 0, problem: 0 };
      dailyMap[dateStr].total++;
      if (cls === "valid") dailyMap[dateStr].valid++;
      else if (cls === "problem") dailyMap[dateStr].problem++;
    }
  }

  const fakePct = totalCalls > 0 ? Math.round((problemCalls / totalCalls) * 100) : 0;

  const series = Object.entries(dailyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({
      label: formatDateLabel(date),
      date,
      total: v.total,
      valid: v.valid,
      problem: v.problem,
      fake_pct: v.total > 0 ? Math.round((v.problem / v.total) * 100) : 0,
    }));

  let change = { total: 0, valid: 0, problem: 0, fake_pct: 0 };
  if (series.length >= 2) {
    const last = series[series.length - 1];
    const prev = series[series.length - 2];
    change.total = prev.total > 0 ? Math.round(((last.total - prev.total) / prev.total) * 100) : 0;
    change.valid = prev.valid > 0 ? Math.round(((last.valid - prev.valid) / prev.valid) * 100) : 0;
    change.problem = prev.problem > 0 ? Math.round(((last.problem - prev.problem) / prev.problem) * 100) : 0;
    change.fake_pct = last.fake_pct - prev.fake_pct;
  }

  return {
    kpi: {
      total_calls: { value: totalCalls, change: change.total },
      valid_calls: { value: validCalls, change: change.valid },
      problem_calls: { value: problemCalls, change: change.problem },
      fake_pct: { value: fakePct, change: change.fake_pct },
    },
    series,
    publishers,
  };
}

/* ---------- KPI Card ---------- */
function KpiCard({ title, value, change, suffix, icon: Icon, color }) {
  const isUp = change > 0;
  const isDown = change < 0;
  const TrendIcon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;

  const changeColor =
    title === "Problem Calls" || title === "Fake Call %"
      ? isUp ? "text-red-400" : isDown ? "text-emerald-400" : "text-slate-500"
      : isUp ? "text-emerald-400" : isDown ? "text-red-400" : "text-slate-500";

  return (
    <div className="bg-[#11141D] border border-white/5 rounded-xl p-5 flex flex-col gap-3 hover:border-white/10 transition-all duration-300" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</span>
        <div className={cn("p-2 rounded-lg", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
          {value}{suffix || ""}
        </span>
      </div>
      <div className={cn("flex items-center gap-1.5 text-xs font-medium", changeColor)}>
        <TrendIcon className="h-3.5 w-3.5" />
        <span>{change === 0 ? "No change" : `${Math.abs(change)}% ${isUp ? "increase" : "decrease"}`} vs prev day</span>
      </div>
    </div>
  );
}

/* ---------- Chart tooltip ---------- */
const chartTooltipStyle = {
  contentStyle: {
    background: "rgba(17, 20, 29, 0.95)",
    border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: "10px",
    padding: "10px 14px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
  labelStyle: { color: "#94a3b8", fontSize: 12, marginBottom: 4 },
  itemStyle: { fontSize: 12, padding: "2px 0" },
};

/* ---------- Insight chip ---------- */
function Insight({ label, value, invert, suffix = "%" }) {
  if (value === 0) return (
    <span className="text-xs text-slate-500">{label}: <span className="text-slate-400">No change</span></span>
  );
  const isGood = invert ? value < 0 : value > 0;
  const color = isGood ? "text-emerald-400" : "text-red-400";
  const word = value > 0 ? "increased" : "decreased";
  return (
    <span className="text-xs text-slate-500">
      {label}: <span className={color}>{word} by {Math.abs(value)}{suffix}</span> vs prev day
    </span>
  );
}

/* ========== MAIN PAGE ========== */
export default function AnalyticsPage() {
  const [rawCalls, setRawCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [publisherFilter, setPublisherFilter] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!dateFilter) return;
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("start_date", dateFilter.startDate);
      params.set("end_date", dateFilter.endDate);
      params.set("limit", "10000");

      const res = await axios.get(`${API}/calls?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRawCalls(Array.isArray(res.data) ? res.data : []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Analytics fetch error:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!dateFilter) return;
    intervalRef.current = setInterval(() => fetchData(true), 90000);
    return () => clearInterval(intervalRef.current);
  }, [dateFilter, fetchData]);

  const { kpi, series, publishers } = useMemo(
    () => computeAnalytics(rawCalls, publisherFilter || null),
    [rawCalls, publisherFilter]
  );

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Analytics</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Performance trends and call quality insights
            {lastUpdated && <span className="ml-1 sm:ml-2 text-slate-600">&middot; Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Publisher filter */}
          <div className="flex items-center gap-2 bg-[#11141D] border border-white/5 rounded-lg px-3 py-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <select
              value={publisherFilter}
              onChange={(e) => setPublisherFilter(e.target.value)}
              className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer max-w-[180px]"
              data-testid="analytics-publisher-filter"
            >
              <option value="" className="bg-[#0a0d14]">All Publishers</option>
              {publishers.map((p) => (
                <option key={p.name} value={p.name} className="bg-[#0a0d14]">{p.name}</option>
              ))}
            </select>
          </div>

          {/* Live indicator */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-[11px] font-semibold text-emerald-400 tracking-wide uppercase">Live</span>
          </div>

          <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all" data-testid="refresh-analytics">
            <RefreshCw className="h-3.5 w-3.5" /><span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      <DateFilter onFilterChange={setDateFilter} defaultPreset="last_30_days" />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard title="Total Calls" value={kpi.total_calls.value} change={kpi.total_calls.change} icon={PhoneCall} color="bg-blue-500/10 text-blue-400" />
            <KpiCard title="Valid Calls" value={kpi.valid_calls.value} change={kpi.valid_calls.change} icon={CheckCircle} color="bg-emerald-500/10 text-emerald-400" />
            <KpiCard title="Problem Calls" value={kpi.problem_calls.value} change={kpi.problem_calls.change} icon={AlertTriangle} color="bg-amber-500/10 text-amber-400" />
            <KpiCard title="Fake Call %" value={kpi.fake_pct.value} change={kpi.fake_pct.change} suffix="%" icon={ShieldAlert} color="bg-red-500/10 text-red-400" />
          </div>

          {/* Calls Over Time */}
          <div className="bg-[#11141D] border border-white/5 rounded-xl p-3 sm:p-5" data-testid="calls-over-time-chart">
            <h3 className="text-sm font-semibold text-slate-300 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Calls Over Time
              <span className="ml-2 text-xs font-normal text-slate-500">(daily)</span>
            </h3>
            {series.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="min-w-[400px]">
                  <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
                  <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip {...chartTooltipStyle} />
                  <Area type="monotone" dataKey="total" name="Total Calls" stroke="#3b82f6" strokeWidth={2} fill="url(#gradTotal)" dot={{ r: 3, fill: "#3b82f6" }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-sm text-slate-500">No data for selected period</div>
            )}
          </div>

          {/* Bottom Row: Fake % Trend + Quality Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Fake Call % Trend */}
            <div className="bg-[#11141D] border border-white/5 rounded-xl p-3 sm:p-5" data-testid="fake-call-trend-chart">
              <h3 className="text-sm font-semibold text-slate-300 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Fake Call % Trend</h3>
              {series.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="min-w-[350px]">
                    <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
                    <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} unit="%" allowDecimals={false} />
                    <Tooltip {...chartTooltipStyle} formatter={(v) => [`${v}%`, "Fake %"]} />
                    <Line type="monotone" dataKey="fake_pct" name="Fake %" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: "#ef4444" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[220px] text-sm text-slate-500">No data for selected period</div>
              )}
            </div>

            {/* Call Quality Trend */}
            <div className="bg-[#11141D] border border-white/5 rounded-xl p-3 sm:p-5" data-testid="call-quality-trend-chart">
              <h3 className="text-sm font-semibold text-slate-300 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Call Quality Trend</h3>
              {series.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="min-w-[350px]">
                    <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
                    <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...chartTooltipStyle} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                    <Bar dataKey="valid" name="Valid" fill="#10b981" radius={[4, 4, 0, 0]} barSize={18} />
                    <Bar dataKey="problem" name="Problem" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[220px] text-sm text-slate-500">No data for selected period</div>
              )}
            </div>
          </div>

          {/* Insights Footer */}
          <div className="bg-[#11141D]/60 border border-white/5 rounded-xl p-4 flex flex-wrap gap-x-8 gap-y-2" data-testid="analytics-insights">
            <Insight label="Calls" value={kpi.total_calls.change} invert={false} />
            <Insight label="Valid Calls" value={kpi.valid_calls.change} invert={false} />
            <Insight label="Problem Calls" value={kpi.problem_calls.change} invert={true} />
            <Insight label="Fake %" value={kpi.fake_pct.change} invert={true} suffix="pp" />
          </div>
        </>
      )}
    </div>
  );
}
